import api from './api';
import {
    ApplicationResult,
    TailorResult,
    ApplicationType,
} from '../types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

export type ApplyStreamEvent = 'progress' | 'result' | 'tailored' | 'done';
export type ApplyStreamHandler = (event: ApplyStreamEvent, payload: any) => void;

const getAuthHeaders = (): Record<string, string> => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
};

const parseSseChunk = (chunk: string, handler: ApplyStreamHandler) => {
    const trimmed = chunk.trim();
    if (!trimmed) return;
    const lines = trimmed.split(/\r?\n/);
    let event: ApplyStreamEvent = 'progress';
    let data = '';

    lines.forEach(line => {
        if (line.startsWith('event:')) {
            event = line.replace('event:', '').trim() as ApplyStreamEvent;
        }
        if (line.startsWith('data:')) {
            data += line.replace('data:', '').trim();
        }
    });

    let payload: any = data;
    if (data) {
        try {
            payload = JSON.parse(data);
        } catch {
            payload = data;
        }
    }

    handler(event, payload);
};

const streamApply = (
    endpoint: 'generate' | 'tailor',
    body: any,
    handler: ApplyStreamHandler
) => {
    const controller = new AbortController();

    const promise = (async () => {
        const response = await fetch(`${API_BASE_URL}/apply/${endpoint}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'text/event-stream',
                ...getAuthHeaders(),
            },
            body: JSON.stringify(body),
            signal: controller.signal,
        });

        if (!response.ok) {
            const payload = await response.json().catch(() => null);
            throw new Error(payload?.message || 'Streaming request failed');
        }

        const reader = response.body?.getReader();
        if (!reader) {
            throw new Error('Failed to read streaming response');
        }

        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
            const { value, done } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const parts = buffer.split('\n\n');
            buffer = parts.pop() || '';
            parts.forEach(part => parseSseChunk(part, handler));
        }

        if (buffer.trim()) {
            parseSseChunk(buffer, handler);
        }
    })();

    return {
        abort: () => controller.abort(),
        promise,
    };
};

export const applyService = {
    async generateApplication(body: {
        type: ApplicationType;
        jobText: string;
        resumeId?: string;
        resumeJson?: any;
        tone?: string;
        length?: string;
        otherLabel?: string;
    }): Promise<ApplicationResult> {
        const response = await api.post<{ success: boolean; data: ApplicationResult }>('/apply/generate', {
            ...body,
        });
        return response.data.data;
    },

    async tailorResume(body: {
        jobText: string;
        resumeId?: string;
        resumeJson?: any;
        templateId?: string;
    }): Promise<TailorResult> {
        const response = await api.post<{ success: boolean; data: TailorResult }>('/apply/tailor', {
            ...body,
        });
        return response.data.data;
    },

    streamGenerate(
        body: {
            type: ApplicationType;
            jobText: string;
            resumeId?: string;
            resumeJson?: any;
            tone?: string;
            length?: string;
            otherLabel?: string;
        },
        handler: ApplyStreamHandler
    ) {
        return streamApply('generate', body, handler);
    },

    streamTailor(
        body: {
            jobText: string;
            resumeId?: string;
            resumeJson?: any;
            templateId?: string;
        },
        handler: ApplyStreamHandler
    ) {
        return streamApply('tailor', body, handler);
    },

    async optimizeText(body: {
        text: string;
        fieldType: 'summary' | 'bullet';
        instruction?: string;
    }): Promise<{ optimizedText: string }> {
        const response = await api.post<{ success: boolean; data: { optimizedText: string } }>('/apply/optimize-text', body);
        return response.data.data;
    },
};
